import React, { useRef } from "react";
import { Input } from "antd";
import { Loader2, Mic, MicOff, Paperclip, Send, X } from "lucide-react";

export interface VoInputProps {
  /** Current text value — voice transcripts or typed text */
  value: string;
  onChange: (value: string) => void;
  /** Called when the user submits (send button or ⌘↵ / Ctrl↵) */
  onSubmit: () => void;
  /** Mic is actively recording */
  isRecording: boolean;
  /** Mic is initializing before recording starts */
  isInitializing: boolean;
  /** Audio transcript is being processed after stopping */
  isProcessingAudio: boolean;
  /** A save/send action is in flight */
  isSubmitting: boolean;
  /** Called to start recording */
  onStartRecording: () => void;
  /** Called to stop recording */
  onStopRecording: () => void;
  disabled?: boolean;
  /** Placeholder text for the textarea. Default: "Or type your notes here…" */
  placeholder?: string;
  className?: string;
  /** Currently attached files */
  attachments?: File[];
  /** Called when user attaches files (via picker or paste) */
  onAttachFiles?: (files: File[]) => void;
  /** Called when user removes an attachment by index */
  onRemoveAttachment?: (index: number) => void;
}

/**
 * VoInput — reusable voice-first input component.
 *
 * A single unified block: prominent "Talk with Vo" button (top) + textarea (bottom)
 * + inline send icon. Use anywhere users need to give Vo a note, a progress update,
 * or any qualitative input.
 *
 * The caller owns `useMicRecorder` from `@vojomo-core/shared` and passes the state
 * and handlers as props.
 *
 * Voice is always the primary CTA. Typing is secondary.
 *
 * @example
 * <VoInput
 *   value={rawInput}
 *   onChange={setRawInput}
 *   onSubmit={handleParseAndSave}
 *   isRecording={isRecording}
 *   isInitializing={isInitializing}
 *   isProcessingAudio={isProcessingAudio}
 *   isSubmitting={isParsing}
 *   onStartRecording={() =>
 *     startRecording(RecordingObjectType.MODUS_CHAT, (t) =>
 *       setRawInput((prev) => (prev ? `${prev} ${t}` : t))
 *     )
 *   }
 *   onStopRecording={stopRecording}
 *   attachments={attachments}
 *   onAttachFiles={setAttachments}
 *   onRemoveAttachment={(i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
 * />
 */
export const VoInput: React.FC<VoInputProps> = ({
  value,
  onChange,
  onSubmit,
  isRecording,
  isInitializing,
  isProcessingAudio,
  isSubmitting,
  disabled,
  placeholder = "Or type your notes here…",
  onStartRecording,
  onStopRecording,
  className,
  attachments = [],
  onAttachFiles,
  onRemoveAttachment,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasAttachments = attachments.length > 0;
  const canSubmit =
    (value.trim().length > 0 || hasAttachments) && !isSubmitting && !isRecording;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) onAttachFiles?.(files);
    // Reset so same file can be re-attached if removed
    e.target.value = "";
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length > 0) {
      e.preventDefault();
      onAttachFiles?.(files);
    }
  };

  return (
    <div
      className={className}
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        overflow: "hidden",
        background: "#fff",
      }}
      onPaste={onAttachFiles ? handlePaste : undefined}
    >
      {/* Voice CTA */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          padding: "12px 16px 10px",
          background: isRecording ? "#fff5f5" : "#fafbff",
          transition: "background 0.2s",
        }}
      >
        <button
          type="button"
          disabled={isInitializing || isProcessingAudio || isSubmitting || disabled}
          onClick={() => (isRecording ? onStopRecording() : onStartRecording())}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 20px",
            borderRadius: 999,
            border: "none",
            fontWeight: 600,
            fontSize: 13,
            minWidth: 148,
            justifyContent: "center",
            cursor:
              isInitializing || isProcessingAudio || isSubmitting || disabled
                ? "not-allowed"
                : "pointer",
            transition: "all 0.15s",
            ...(isRecording
              ? {
                  background: "#ef4444",
                  color: "#fff",
                  boxShadow: "0 2px 12px rgba(239,68,68,0.3)",
                }
              : isInitializing || isProcessingAudio
              ? { background: "rgba(44,21,97,0.5)", color: "#fff" }
              : {
                  background: "#2c1561",
                  color: "#fff",
                  boxShadow: "0 1px 4px rgba(44,21,97,0.2)",
                }),
          }}
        >
          {isProcessingAudio ? (
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
          ) : isRecording ? (
            <MicOff style={{ width: 14, height: 14 }} />
          ) : (
            <Mic style={{ width: 14, height: 14 }} />
          )}
          <span>
            {isProcessingAudio
              ? "Processing..."
              : isRecording
              ? "Stop recording"
              : "Talk with Vo"}
          </span>
        </button>
        <p
          style={{
            fontSize: 10,
            color: isRecording ? "#ef4444" : "#94a3b8",
            margin: 0,
            textAlign: "center",
            lineHeight: 1.4,
            transition: "color 0.2s",
          }}
        >
          {isRecording
            ? "Listening… click to stop"
            : "Don't overthink it — just talk through your update"}
        </p>
      </div>

      {/* Attachment chips */}
      {hasAttachments && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "8px 12px 0",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          {attachments.map((file, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px 2px 6px",
                borderRadius: 999,
                background: "#f1f5f9",
                fontSize: 11,
                color: "#475569",
                maxWidth: 180,
              }}
            >
              <Paperclip style={{ width: 10, height: 10, flexShrink: 0 }} />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 130,
                }}
              >
                {file.name}
              </span>
              {onRemoveAttachment && (
                <button
                  type="button"
                  onClick={() => onRemoveAttachment(i)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: "#94a3b8",
                    flexShrink: 0,
                  }}
                >
                  <X style={{ width: 10, height: 10 }} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Text input */}
      <div style={{ position: "relative", borderTop: "1px solid #f1f5f9" }}>
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 5 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={isSubmitting || isRecording || disabled}
          bordered={false}
          style={{
            fontSize: 13,
            color: "#334155",
            paddingLeft: onAttachFiles ? 42 : undefined,
            paddingRight: 42,
            resize: "none",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSubmit) {
              e.preventDefault();
              onSubmit();
            }
          }}
        />

        {/* Paperclip — bottom-left, only when attachment support is enabled */}
        {onAttachFiles && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting || disabled}
              title="Attach file"
              style={{
                position: "absolute",
                left: 10,
                bottom: 10,
                width: 26,
                height: 26,
                borderRadius: 6,
                border: "none",
                cursor: isSubmitting || disabled ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                color: "#94a3b8",
                transition: "color 0.15s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && !disabled)
                  (e.currentTarget as HTMLButtonElement).style.color = "#475569";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = "#94a3b8";
              }}
            >
              <Paperclip style={{ width: 13, height: 13 }} />
            </button>
          </>
        )}

        {/* Send — bottom-right */}
        <button
          type="button"
          onClick={() => { console.warn(`[VoInput] send clicked: canSubmit=${canSubmit} value="${value.slice(0,30)}…" isSubmitting=${isSubmitting}`); onSubmit(); }}
          disabled={!canSubmit}
          title={isSubmitting ? "Saving…" : "Update with Vo (⌘↵)"}
          style={{
            position: "absolute",
            right: 10,
            bottom: 10,
            width: 26,
            height: 26,
            borderRadius: 6,
            border: "none",
            cursor: canSubmit ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s, color 0.15s",
            background: canSubmit ? "#2c1561" : "#e2e8f0",
            color: canSubmit ? "#fff" : "#94a3b8",
            flexShrink: 0,
          }}
        >
          {isSubmitting ? (
            <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
          ) : (
            <Send style={{ width: 12, height: 12 }} />
          )}
        </button>
      </div>
    </div>
  );
};
